import {
  buildThemeFromServerTheme,
  BuyBoxDialog,
  CustomThemeProvider,
  DEFAULT_THEME_OPTIONS,
  getRootFontSize,
  useUpdateCustomTheme,
} from '@brandclub/common-ui';
import type { BcMountSource } from './index';
import { styled, StyledEngineProvider, ThemeOptions } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import _isEqual from 'lodash/isEqual';
import React, { lazy, PropsWithChildren, Suspense, useEffect, useMemo, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { createPortal } from 'react-dom';
import { appThemeQueryOptions } from './api/cache/appThemeQueryOptions';
import { themeSetupQueryOptions } from './api/cache/themeSetupQueryOptions';
import ShareProductDialog from './components/dialog/ShareDialog';
import InPageAddToBagButton from './modules/AddToBagButton/InpageAddToBagButton';
import BCBagButton from './modules/BagButton/BCBagButton';
import BCCheckoutBar, { BCCheckoutBarProps } from './modules/CheckoutBar/BCCheckoutBar';
import GetAppBanners from './modules/GetAppBanners';
import MembershipButton from './modules/MembershipButton/MembershipButton';
import BCProfileButton from './modules/ProfileButton/BCProfileButton';
import { ThemeManager } from '@brandclub/bc-widgets';

import PurchaseRewardDialog from './components/dialog/PurchaseRewardDialog';
import AddToBagForSku from './modules/AddToBagButton/AddToBagForSku';
import LocationMapForSku from './modules/StoreLocator/LocationMapForSku';
import { LocationMapModal } from './modules/StoreLocator/StoreLocator';
import BagDrawer from './modules/BagButton/BagDrawer';
import { ProductRewardSearchResult } from '@brandclub/common-ui';
import { brandclubProductQueryOptions } from './api/cache/dtcProductQueryOptions';
import { SearchButton } from './modules/Search/SearchButton';
import { useStoreLocatorIntent } from './modules/StoreLocator/useStoreLocatorIntent';
import AddToBagController from './modules/AddToBagButton/AddToBagController';
import { useElementObserver } from './hooks/useElementObserver';
import { ManagedPortal } from './components/ManagedPortal';
import { HOST_DOMAIN } from './utils/domain';
import { getPagesShowCheckoutFromMeta } from './utils/bcProductMetadataUtils';

// Lazy load components for better code splitting
const ThemeLiveSetup = lazy(() => import('./components/theme-setup/LiveThemeSetup'));

type ConciergeButtonProps = React.ComponentProps<(typeof import('./modules/Concierge'))['ConciergeButton']>;

// The concierge button and its widget registry load in the same lazy step.
// The registry module starts the module federation runtime and fetches the
// concierge widget manifest when it loads. A static import put about 60 KB gzip
// in the main file and ran that start-up during bundle evaluation. The registry
// context is read only inside the concierge tree, so nothing outside it waits.
const ConciergeButton = lazy(() =>
  Promise.all([import('./modules/Concierge'), import('./modules/Concierge/MfWidgetRegistryProvider')]).then(
    ([concierge, registry]) => {
      const ConciergeButtonWithRegistry = (props: ConciergeButtonProps) => (
        <registry.MfWidgetRegistryProvider>
          <concierge.ConciergeButton {...props} />
        </registry.MfWidgetRegistryProvider>
      );
      return { default: ConciergeButtonWithRegistry };
    },
  ),
);

const FontFamilyWrapper = styled('div')(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
}));

const useElementsObserverAll = (selector: string) => {
  const [elements, setElements] = useState<Element[]>([]);

  useEffect(() => {
    const queryAll = () => Array.from(document.querySelectorAll(selector));
    const update = () => setElements(queryAll());
    update();
    const observer = new MutationObserver(() => update());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [selector]);

  return elements;
};

const SkuButtonPortal = ({
  host,
  sku,
  product,
}: {
  host: Element;
  sku: string;
  product: ProductRewardSearchResult;
}) => {
  return (
    <ManagedPortal host={host} name={`add-to-bag-sku-${sku}`}>
      <AddToBagForSku product={product} buyButtonOnly />
    </ManagedPortal>
  );
};

const SkuButtonsPortals = () => {
  const skuHosts = useElementsObserverAll('.bc-addtobag-sku[data-stackline-sku]');
  const [productsBySku, setProductsBySku] = useState<Record<string, ProductRewardSearchResult>>({});
  const queryClient = useQueryClient();

  useEffect(() => {
    const skus = Array.from(
      new Set(skuHosts.map((el) => (el as HTMLElement).dataset.stacklineSku || '').filter(Boolean)),
    );
    if (skus.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const products = await Promise.all(
          skus.map((sku) => queryClient.ensureQueryData(brandclubProductQueryOptions(sku))),
        );
        if (cancelled) return;
        const map: Record<string, ProductRewardSearchResult> = {};
        for (const p of products) {
          if (p?.stacklineSku) map[p.stacklineSku] = p as any;
        }
        setProductsBySku(map);
      } catch (e) {
        console.error('Failed to fetch products for add-to-bag buttons', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryClient, skuHosts.map((e) => (e as HTMLElement).dataset.stacklineSku).join(',')]);

  return (
    <>
      {skuHosts.map((host, index) => {
        const sku = (host as HTMLElement).dataset.stacklineSku || '';
        const product = productsBySku[sku];
        return product ? <SkuButtonPortal key={`${sku}-${index}`} host={host} sku={sku} product={product} /> : null;
      })}
    </>
  );
};

const SkuLocatorPortal = ({
  host,
  sku,
  product,
  mobile,
}: {
  host: Element;
  sku: string;
  product: ProductRewardSearchResult;
  mobile: boolean;
}) => {
  return (
    <ManagedPortal host={host} name={`location-map-sku-${sku}`}>
      <LocationMapForSku product={product} mobile={mobile} />
    </ManagedPortal>
  );
};

const SkuLocatorPortals = () => {
  const skuHosts = useElementsObserverAll('.bc-location-map-sku[data-stackline-sku]');
  const [productsBySku, setProductsBySku] = useState<Record<string, ProductRewardSearchResult>>({});
  const queryClient = useQueryClient();

  useEffect(() => {
    const skus = Array.from(
      new Set(skuHosts.map((el) => (el as HTMLElement).dataset.stacklineSku || '').filter(Boolean)),
    );
    if (skus.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const products = await Promise.all(
          skus.map((sku) => queryClient.ensureQueryData(brandclubProductQueryOptions(sku))),
        );
        if (cancelled) return;
        const map: Record<string, ProductRewardSearchResult> = {};
        for (const p of products) {
          if (p?.stacklineSku) map[p.stacklineSku] = p as any;
        }
        setProductsBySku(map);
      } catch (e) {
        console.error('Failed to fetch products for store locator portals', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryClient, skuHosts.map((e) => (e as HTMLElement).dataset.stacklineSku).join(',')]);

  return (
    <>
      {skuHosts.map((host, index) => {
        const el = host as HTMLElement;
        const sku = el.dataset.stacklineSku || '';
        const product = productsBySku[sku];
        const mobile = el.dataset.bcMobile === 'true' || el.dataset.mobile === 'true';
        return product ? (
          <SkuLocatorPortal key={`${sku}-${index}`} host={host} sku={sku} product={product} mobile={mobile} />
        ) : null;
      })}
    </>
  );
};

const InAppCheckout = () => {
  const searchBarElement = useElementObserver('.bc-search');
  const bagButton = useElementObserver('.bc-header-cart-icon');
  const bagButtonMobile = useElementObserver('.bc-header-cart-icon-mobile');

  const membershipButton = useElementObserver('.bc-header-membership-icon');
  const membershipButtonMobile = useElementObserver('.bc-header-membership-icon-mobile');

  const profileButton = useElementObserver('.bc-header-profile-icon');
  const profileButtonMobile = useElementObserver('.bc-header-profile-icon-mobile');

  const inPageAddToBagButton = useElementObserver('.bc-inpage-addtobag-button', false);
  const inPageAddToBagButtonMobile = useElementObserver('.bc-inpage-addtobag-button-mobile', false);

  return (
    <>
      <ManagedPortal host={bagButton} name="bag-button">
        <BCBagButton />
      </ManagedPortal>
      <ManagedPortal host={bagButtonMobile} name="bag-button-mobile">
        <BCBagButton mobile />
      </ManagedPortal>

      <ManagedPortal host={membershipButton} name="membership-button">
        <MembershipButton />
      </ManagedPortal>
      <ManagedPortal host={membershipButtonMobile} name="membership-button-mobile">
        <MembershipButton mobile />
      </ManagedPortal>

      <ManagedPortal host={profileButton} name="profile-button">
        <BCProfileButton />
      </ManagedPortal>
      <ManagedPortal host={profileButtonMobile} name="profile-button-mobile">
        <BCProfileButton mobile />
      </ManagedPortal>

      <ManagedPortal host={inPageAddToBagButton} name="inpage-add-to-bag">
        <InPageAddToBagButton />
      </ManagedPortal>
      <ManagedPortal host={inPageAddToBagButtonMobile} name="inpage-add-to-bag-mobile">
        <InPageAddToBagButton mobile />
      </ManagedPortal>

      {searchBarElement && createPortal(<SearchButton />, searchBarElement)}

      {/* multi-sku buttons */}
      <SkuButtonsPortals />
      {/* store locator by sku (host: .bc-location-map-sku[data-stackline-sku]) */}
      <SkuLocatorPortals />
    </>
  );
};

/**
 * Listens for imperative store-locator requests (from `window.BCCheckoutBar.openStoreLocator`)
 * and opens `LocationMapModal` with the product loaded from the cache.
 * Renders regardless of DTC mode so hosts driving us via the imperative API
 * work even when the shop-app is otherwise headless on this page.
 */
const StoreLocatorController: React.FC = () => {
  const stacklineSku = useStoreLocatorIntent((s) => s.stacklineSku);
  const clear = useStoreLocatorIntent((s) => s.clear);

  const { data: product } = useQuery({
    ...brandclubProductQueryOptions(stacklineSku || undefined),
    enabled: !!stacklineSku,
  });

  const open = !!stacklineSku;

  return (
    <LocationMapModal
      open={open}
      onClose={clear}
      stacklineSku={stacklineSku || undefined}
      product={product}
      API_URL={HOST_DOMAIN}
    />
  );
};

/**
 * Injects a one-time global stylesheet that ensures our BC icon-button slots
 * and their direct ancestors never clip child content. This lets labels placed
 * inside the buttons overflow the slot bounds and remain visible even when the
 * host page's CSS sets overflow:hidden on ancestor containers.
 */
const BC_SLOT_OVERFLOW_STYLE_ID = 'bc-slot-overflow-styles';
const BC_SLOT_SELECTORS = [
  '.bc-header-cart-icon',
  '.bc-header-profile-icon',
  '.bc-header-membership-icon',
  '.bc-header-cart-icon-mobile',
  '.bc-header-profile-icon-mobile',
  '.bc-header-membership-icon-mobile',
];
const injectSlotOverflowStyles = () => {
  if (document.getElementById(BC_SLOT_OVERFLOW_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = BC_SLOT_OVERFLOW_STYLE_ID;
  const slots = BC_SLOT_SELECTORS.join(',\n');
  // :has() targets direct parent containers without knowing their class names.
  const parents = BC_SLOT_SELECTORS.map((s) => `:has(> ${s})`).join(',\n');
  style.textContent = `
${slots} { overflow: visible !important; }
${parents} { overflow: visible !important; }
  `.trim();
  document.head.appendChild(style);
};

const ThemeInitializer = (props: PropsWithChildren) => {
  const [prevThemeOptions, setThemeOptions] = useUpdateCustomTheme();
  const { data: styleData } = useQuery(appThemeQueryOptions());

  useEffect(() => {
    injectSlotOverflowStyles();
  }, []);

  useEffect(() => {
    const parsedTheme = buildThemeFromServerTheme(styleData);
    if (parsedTheme && !_isEqual(parsedTheme, prevThemeOptions)) {
      setThemeOptions(parsedTheme);
    }
  }, [styleData]);

  useEffect(() => {
    const link = document.createElement('link');
    if (styleData?.settings?.fonts) {
      if (styleData.settings.fonts.fontFamilyLink) {
        // download the font from the link and apply so it's available for the theme
        link.rel = 'stylesheet';
        link.href = styleData.settings.fonts.fontFamilyLink;
        document.head.appendChild(link);
      }
    }
    if (styleData?.customCss) {
      const style = document.createElement('style');
      style.textContent = styleData.customCss;
      document.head.appendChild(style);
    }
  }, [styleData]);

  return <FontFamilyWrapper>{props.children}</FontFamilyWrapper>;
};

const CheckoutComponent = () => {
  const [customTheme] = useUpdateCustomTheme();
  // Default to in page checkout when custom theme data is missing to prevent checkout bar flashing during load
  const activeCheckoutBar = customTheme?.dtcMode === 'checkout-bar';
  if (!customTheme?.content) {
    // if the custom theme content is not loaded, we don't want to render the checkout bar or in app checkout to prevent flashing during load
    return null;
  }

  return activeCheckoutBar ? <BCCheckoutBar /> : <InAppCheckout />;
};

const CheckoutShopApp: React.FC<
  BCCheckoutBarProps & { mountSource?: BcMountSource; showConcierge?: boolean; showCheckout?: boolean }
> = ({ mountSource, showConcierge, showCheckout = true }) => {
  const [pagesShowCheckout, setPagesShowCheckout] = useState<boolean | undefined>(() =>
    mountSource === 'pages-storefront' ? getPagesShowCheckoutFromMeta() : undefined,
  );
  useEffect(() => {
    if (mountSource !== 'pages-storefront') return;
    const sync = () => setPagesShowCheckout(getPagesShowCheckoutFromMeta());
    sync();
    window.addEventListener('bcProductMetaDataChanged', sync);
    const interval = window.setInterval(sync, 500);
    return () => {
      window.removeEventListener('bcProductMetaDataChanged', sync);
      window.clearInterval(interval);
    };
  }, [mountSource]);
  const pagesCheckoutOff =
    mountSource === 'pages-storefront' && (showCheckout === false || pagesShowCheckout === false);
  const cartEnabled = pagesCheckoutOff ? false : showCheckout;

  const { data: themeSetupResponse } = useQuery(themeSetupQueryOptions());
  const themeOptions = useMemo(() => {
    return produce(DEFAULT_THEME_OPTIONS as ThemeOptions, (draft) => {
      if (typeof draft.typography === 'function') {
        return;
      }
      draft.typography ??= {};
      // this is required for sites that modify the root font size for their own rem calculations. we must ensure that the theme is using the same root font size as the site to avoid incorrect font sizes.
      draft.typography.htmlFontSize = getRootFontSize();
      return;
    });
  }, []);

  return (
    <CookiesProvider>
      <StyledEngineProvider injectFirst>
        <CustomThemeProvider themeOptions={themeOptions}>
          <ThemeInitializer>
            <ThemeManager />
            <Suspense fallback={null}>{!!themeSetupResponse?.setupId && <ThemeLiveSetup />}</Suspense>
            <BuyBoxDialog />
            <PurchaseRewardDialog />
            {/* <AutoShowMembershipDialog /> */}
            <ShareProductDialog />
            {/* The BRANDCLUB CART. Gated as one block by the store's CHECKOUT
                capability (D-49) — the bag drawer, the bar/in-page portals and
                the imperative add-to-bag listener are one feature, and mounting
                any part of it without the rest gives a shopper a button that
                leads nowhere. Defaults to on, so every host that does not pass
                the flag behaves exactly as before.

                `StoreLocatorController` stays outside: where-to-buy is what a
                brand WITHOUT checkout shows instead of a cart, so the
                imperative `openStoreLocator` API must survive the gate. The
                by-sku locator portals do ride inside `InAppCheckout` and so are
                gated with it — no store on the App Proxy surface has both that
                markup and checkout off today, and untangling them is a
                different change. */}
            {cartEnabled && (
              <>
                <BagDrawer />
                <CheckoutComponent />
                <AddToBagController />
              </>
            )}
            <GetAppBanners />
            <StoreLocatorController />
            <Suspense fallback={null}>
              <ConciergeButton mountSource={mountSource} showConcierge={showConcierge} cartEnabled={cartEnabled} />
            </Suspense>
          </ThemeInitializer>
        </CustomThemeProvider>
      </StyledEngineProvider>
    </CookiesProvider>
  );
};

export default CheckoutShopApp;
